import React, { useMemo, useCallback } from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactECharts from 'echarts-for-react';
import LoaderComponent from 'components/Loader';
import { LongArrowDown, LongArrowUp } from 'components/CommonIcons/Icons';
import { barChartBuilder } from '../Dropdown/barChartBuilder';
import PipelineStatusTable from './PipelineStatusTable';

type ChartOrientation = 'vertical' | 'horizontal';
type ChartMode = 'stacked' | 'grouped';

type SortInfo = {
  sortBy: string;
  sortDir?: string;
};

interface ChartSeriesItem {
  name?: string;
  color?: string;
  data?: number[];
  [key: string]: unknown;
}

interface TableColumn {
  name: string;
  key?: string;
  [key: string]: unknown;
}

interface TableRow {
  [key: string]: unknown;
}

export interface DashboardSplitViewProps {
  title?: string;
  chartCategories: string[];
  chartSeries: ChartSeriesItem[];
  tableColumns: TableColumn[];
  tableData: TableRow[];
  isLoading?: boolean;
  totalSubtitle?: string;
  totalTrend?: 'up' | 'down' | 'neutral';
  isScheduleByHour?: boolean;
}

interface PipelineStatusChartProps {
  title: string;
  categories: string[];
  series: ChartSeriesItem[];
  isLoading?: boolean;
  orientation?: ChartOrientation;
  mode?: ChartMode;
  height?: number | string;
  showBarLabel?: boolean;
  barWidth?: number | string;
  showLegendOnTop?: boolean;
}

interface BuildChartOptionsParams {
  categories: string[];
  series: ChartSeriesItem[];
  orientation: ChartOrientation;
  mode: ChartMode;
  palette: unknown;
  showBarLabel: boolean;
  showLegendOnTop: boolean;
  barWidth: number | string;
}

const DEFAULT_TITLE_COLOR = '#0D3369';
const DASHBOARD_PANEL_HEIGHT = 668;
const DEFAULT_CHART_HEIGHT = 360;
const DEFAULT_BAR_WIDTH = 24;
const DEFAULT_SORTING_INFO: SortInfo = { sortBy: '', sortDir: '' };

const DEFAULT_SERIES_COLORS: Record<string, string> = {
  Successful: '#01C008',
  Failed: '#FE2626',
  Aborted: '#F79227',
  'In Progress': '#ECB914',
};

const chartStyles = {
  wrapper: {
    width: '100%',
    mb: 2,
  },
  title: (color: string) => ({
    color,
    mb: 1,
    display: 'block',
    fontWeight: 600,
    fontSize: '24px',
  }),
  chartContainer: (height: number | string) => ({
    width: '100%',
    height,
    backgroundColor: '#EDF5FF',
    borderRadius: '4px',
    padding: { xs: '8px', sm: '12px' },
    boxSizing: 'border-box',
    position: 'relative',
  }),
} as const;

const resolveSeriesColor = (seriesItem: ChartSeriesItem): string | undefined => {
  if (typeof seriesItem.color === 'string' && seriesItem.color.length > 0) {
    return seriesItem.color;
  }

  if (typeof seriesItem.name !== 'string') {
    return undefined;
  }

  return DEFAULT_SERIES_COLORS[seriesItem.name];
};

const normalizeChartSeries = (series: ChartSeriesItem[] = []): ChartSeriesItem[] =>
  series.map((seriesItem) => ({
    ...seriesItem,
    color: resolveSeriesColor(seriesItem),
  }));

const withBarWidthAndSpacing = (
  options: any,
  barWidth: number | string,
  orientation: ChartOrientation,
) => {
  
  const updatedOptions = {
    ...options,
    backgroundColor: 'transparent',
    grid: {
      ...options?.grid,
      top: 50,
      bottom: 24,
      left: 50,
      right: 16,
      containLabel: true,
    },
    series: Array.isArray(options?.series)
      ? options.series.map((chartSeries: any) => ({
          ...chartSeries,
          barWidth,
          barMaxWidth: barWidth,
          barCategoryGap: '35%',
          barGap: '10%'
        }))
      : options?.series,
  };

  if (orientation === 'horizontal') {
    const yAxisBase = Array.isArray(updatedOptions?.yAxis)
      ? updatedOptions.yAxis[0]
      : (updatedOptions?.yAxis ?? {});
    const xAxisBase = Array.isArray(updatedOptions?.xAxis)
      ? updatedOptions.xAxis[0]
      : (updatedOptions?.xAxis ?? {});

    return {
      ...updatedOptions,
      yAxis: [
        {
          ...yAxisBase,
          type: 'category',
          inverse: true,
          axisTick: { show: false },
          axisLine: {
            ...yAxisBase?.axisLine,
            show: true,
          },
          axisLabel: {
            ...yAxisBase?.axisLabel,
            margin: 8,
          },
        },
      ],
      xAxis: [
        {
          ...xAxisBase,
          splitLine: { show: false },
          axisLabel: {
            ...xAxisBase?.axisLabel,
            formatter: (value: number) => value.toLocaleString(),
          },
        },
      ],
    };
  }

  return updatedOptions;
};

const buildChartOptions = ({
  categories,
  series,
  orientation,
  mode,
  palette,
  showBarLabel,
  showLegendOnTop,
  barWidth,
}: BuildChartOptionsParams) => {
  const options = barChartBuilder(
    categories,
    series,
    {
      orientation,
      mode,
      colors: palette,
      showGridLines: false,
      showBarLabel,
      showLegendOnTop,
    },
  );

  if (!options) {
    return null;
  }

  return withBarWidthAndSpacing(options, barWidth, orientation);
};

const ChartTitle: React.FC<{ title: string; color: string }> = ({ title, color }) => {
  if (!title) {
    return null;
  }

  return (
    <Typography variant="widgetTitleNew" sx={chartStyles.title(color)}>
      {title}
    </Typography>
  );
};

const ChartBody: React.FC<{
  isLoading: boolean;
  chartOptions: unknown;
  height: number | string;
}> = ({ isLoading, chartOptions, height }) => {
  let content;

  if (isLoading) {
    content = <LoaderComponent pt={20} />;
  } else if (chartOptions) {
    content = (
      <ReactECharts
        option={chartOptions}
        style={{ width: '100%', height: '100%' }}
        notMerge={true}
      />
    );
  } else {
    content = (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: '#A7ADCB', fontSize: '14px', fontWeight: 400 }}
        >
          No data found
        </Typography>
      </Box>
    );
  }

  const isNoData = !isLoading && !chartOptions;
  const containerStyle = {
    ...chartStyles.chartContainer(height),
    backgroundColor: isNoData ? 'transparent' : '#EDF5FF',
  };

  return <Box sx={containerStyle}>{content}</Box>;
};

const isNilLike = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || value === '-';

const isNumericValue = (value: unknown): boolean => {
  if (typeof value === 'number') return !Number.isNaN(value);
  if (typeof value !== 'string') return false;

  const cleaned = value.split(',').join('').trim();
  return cleaned !== '' && !Number.isNaN(Number(cleaned));
};

const parseRangeStart = (value: string): number | null => {
  const rangePattern = /^(\d+)\s*-\s*(\d+)$/;
  const match = rangePattern.exec(value.trim());
  return match ? Number(match[1]) : null;
};

const normalizeComparableValue = (value: unknown): string | number => {
  if (isNilLike(value)) return '';

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    const rangeStart = parseRangeStart(trimmed);
    if (rangeStart !== null) {
      return rangeStart;
    }

    if (isNumericValue(trimmed)) {
      return Number(trimmed.split(',').join(''));
    }

    const dateTime = new Date(trimmed).getTime();
    if (!Number.isNaN(dateTime)) {
      return dateTime;
    }

    return trimmed.toLowerCase();
  }

  return String(value).toLowerCase();
};

const compareValues = (left: unknown, right: unknown): number => {
  const leftEmpty = isNilLike(left);
  const rightEmpty = isNilLike(right);

  if (leftEmpty && rightEmpty) return 0;
  if (leftEmpty) return 1;
  if (rightEmpty) return -1;

  const a = normalizeComparableValue(left);
  const b = normalizeComparableValue(right);

  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const isDescendingSort = (sortDir?: string): boolean => {
  if (!sortDir) return false;

  const normalized = sortDir.trim().toUpperCase();
  return (
    normalized === 'DESC' ||
    normalized === 'DSC' ||
    normalized === 'Z-A' ||
    normalized === 'ZA' ||
    normalized === 'DEC-ASC' ||
    normalized === 'DEC - ASC'
  );
};

const sortRows = (rows: TableRow[], sortingInfo: SortInfo): TableRow[] => {
  const { sortBy, sortDir } = sortingInfo;

  if (!sortBy || !sortDir) {
    return rows;
  }

  const sortedRows = [...rows].sort((a, b) => compareValues(a[sortBy], b[sortBy]));

  return isDescendingSort(sortDir) ? sortedRows.reverse() : sortedRows;
};

type LayoutBoxSx = {
  height?: number;
  overflowY?: string;
  overflowX?: string;
  borderRadius?: string;
};

interface DashboardLayoutConfig {
  isVerticalLayout: boolean;
  fullChartHeight: number;
  visibleChartHeight: number;
  barWidth: number;
  showBarLabel: boolean;
  tableHeight: number | string;
  splitLayoutChartBoxSx: LayoutBoxSx;
  splitLayoutTableBoxSx: LayoutBoxSx;
}

const DASHBOARD_LAYOUT_ROW_HEIGHT = 45;
const DASHBOARD_LAYOUT_CHART_OVERHEAD = 80;
const DASHBOARD_LAYOUT_MAX_VISIBLE_ROWS = 10;
const DASHBOARD_LAYOUT_FIXED_HEIGHT = 600;
const VERTICAL_LAYOUT_HEIGHT = 300;
const SCHEDULE_BY_HOUR_BAR_WIDTH = 26;
const SPLIT_LAYOUT_BAR_WIDTH = 20;

const getFullChartHeight = (
  isScheduleByHour: boolean,
  isVerticalLayout: boolean,
  calculatedChartHeight: number,
): number => {
  if (isScheduleByHour) {
    return Math.max(DASHBOARD_LAYOUT_FIXED_HEIGHT, calculatedChartHeight);
  }

  if (isVerticalLayout) {
    return VERTICAL_LAYOUT_HEIGHT;
  }

  return Math.max(DASHBOARD_PANEL_HEIGHT, calculatedChartHeight);
};

const getVisibleChartHeight = (
  isScheduleByHour: boolean,
  isVerticalLayout: boolean,
  fullChartHeight: number,
): number => {
  if (isScheduleByHour) {
    return DASHBOARD_LAYOUT_FIXED_HEIGHT;
  }

  if (isVerticalLayout) {
    return VERTICAL_LAYOUT_HEIGHT;
  }

  return Math.min(
    fullChartHeight,
    DASHBOARD_LAYOUT_MAX_VISIBLE_ROWS * DASHBOARD_LAYOUT_ROW_HEIGHT +
      DASHBOARD_LAYOUT_CHART_OVERHEAD,
  );
};

const getBarWidth = (isScheduleByHour: boolean, isVerticalLayout: boolean): number => {
  if (isScheduleByHour) {
    return SCHEDULE_BY_HOUR_BAR_WIDTH;
  }

  return isVerticalLayout ? DEFAULT_BAR_WIDTH : SPLIT_LAYOUT_BAR_WIDTH;
};

const getTableHeight = (
  isScheduleByHour: boolean,
  isVerticalLayout: boolean,
  visibleChartHeight: number,
): number | string => {
  if (isScheduleByHour) {
    return DASHBOARD_LAYOUT_FIXED_HEIGHT;
  }

  return isVerticalLayout ? 'auto' : visibleChartHeight;
};

const getSplitLayoutChartBoxSx = (
  isVerticalLayout: boolean,
  visibleChartHeight: number,
): LayoutBoxSx => {
  if (isVerticalLayout) {
    return {};
  }

  return { height: visibleChartHeight, overflowY: 'auto', borderRadius: '4px' };
};

const getSplitLayoutTableBoxSx = (
  isVerticalLayout: boolean,
  visibleChartHeight: number,
  isScheduleByHour: boolean,
): LayoutBoxSx => {
  if (isVerticalLayout) {
    return {};
  }

  return {
    height: visibleChartHeight,
    overflowY: isScheduleByHour ? 'visible' : 'hidden',
    overflowX: isScheduleByHour ? 'visible' : 'auto',
  };
};

const getDashboardLayoutConfig = (
  isScheduleByHour: boolean,
  categoryCount: number,
): DashboardLayoutConfig => {
  const isVerticalLayout = !isScheduleByHour && categoryCount <= 5;
  const calculatedChartHeight =
    categoryCount * DASHBOARD_LAYOUT_ROW_HEIGHT + DASHBOARD_LAYOUT_CHART_OVERHEAD;
  const fullChartHeight = getFullChartHeight(
    isScheduleByHour,
    isVerticalLayout,
    calculatedChartHeight,
  );
  const visibleChartHeight = getVisibleChartHeight(
    isScheduleByHour,
    isVerticalLayout,
    fullChartHeight,
  );

  return {
    isVerticalLayout,
    fullChartHeight,
    visibleChartHeight,
    barWidth: getBarWidth(isScheduleByHour, isVerticalLayout),
    showBarLabel: isVerticalLayout,
    tableHeight: getTableHeight(
      isScheduleByHour,
      isVerticalLayout,
      visibleChartHeight,
    ),
    splitLayoutChartBoxSx: getSplitLayoutChartBoxSx(
      isVerticalLayout,
      visibleChartHeight,
    ),
    splitLayoutTableBoxSx: getSplitLayoutTableBoxSx(
      isVerticalLayout,
      visibleChartHeight,
      isScheduleByHour,
    ),
  };
};

export const DashboardSplitView: React.FC<DashboardSplitViewProps> = ({
  title,
  chartCategories,
  chartSeries,
  tableColumns,
  tableData,
  isLoading,
  totalSubtitle,
  totalTrend,
  isScheduleByHour = false,
}) => {
  const [sortingInfo, setSortingInfo] = React.useState<SortInfo>(DEFAULT_SORTING_INFO);

  const handleSorting = useCallback((nextSortingInfo: SortInfo) => {
    setSortingInfo(nextSortingInfo);
  }, []);

  const sortedTableData = useMemo(
    () => sortRows(tableData, sortingInfo),
    [tableData, sortingInfo],
  );

  const theme = useTheme();

  const normalizedSeries = useMemo(() => normalizeChartSeries(chartSeries), [chartSeries]);
  const categoryCount = chartCategories.length;
  const {
    isVerticalLayout,
    fullChartHeight,
    barWidth,
    showBarLabel,
    tableHeight,
    splitLayoutChartBoxSx,
    splitLayoutTableBoxSx,
    visibleChartHeight,
  } = getDashboardLayoutConfig(isScheduleByHour, categoryCount);

  const renderHeader = (headerTitle?: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, mt: 1, pl: 0, gap: 1.5 }}>
      <Typography
        variant="h2"
        sx={{
          fontWeight: 500,
          fontSize: '30px',
          color: '#003771',
        }}
      >
        {headerTitle}
      </Typography>
      {totalSubtitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 0.5 }}>
          {totalTrend === 'up' && <LongArrowUp color={theme.palette.success.main} />}
          {totalTrend === 'down' && <LongArrowDown color={theme.palette.error.main} />}
          <Typography variant="body1" sx={{ color: '#003771', opacity: 0.7, fontWeight: 500 }}>
            {totalSubtitle}
          </Typography>
        </Box>
      )}
    </Box>
  );
  const isDataLoading = Boolean(isLoading);

  if (isDataLoading) {
    return (
      <Box sx={{ width: '100%' }}>
        {renderHeader(title)}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
          }}
        >
          <LoaderComponent />
        </Box>
      </Box>
    );
  }

  const chartElement = (
    <Box sx={splitLayoutChartBoxSx}>
      <PipelineStatusChart
        title=""
        categories={chartCategories}
        series={normalizedSeries}
        isLoading={isDataLoading}
        barWidth={barWidth}
        height={fullChartHeight}
        showBarLabel={showBarLabel}
        showLegendOnTop={true}
      />
    </Box>
  );

  const tableElement = (
    <Box sx={{
      ...splitLayoutTableBoxSx,
      ...(isScheduleByHour && {
        '& .rdg': {
          height: `${visibleChartHeight}px !important`,
          maxHeight: `${visibleChartHeight}px !important`,
          overflowY: 'auto !important',
        }
      })
    }}>
      <PipelineStatusTable
        moveToolbarAboveTable={isScheduleByHour}
        columns={tableColumns}
        rows={sortedTableData}
        onSorting={handleSorting}
        sortingInfo={sortingInfo}
        height={isScheduleByHour ? visibleChartHeight : tableHeight}
        hideTopBar={false}
        isRenderPagination={false}
        showMoreButton={true}
        isPrevNextPagination = {false}
      />
    </Box>
  );
  return (
    <Box sx={{ width: '100%' }}>
      {renderHeader(title)}

      <Grid container sx={{ mt: 0 }}>
        {isVerticalLayout ? (
          <Grid item xs={12} sx={{ px:0 }}>
            <Box sx={{ width: '100%' }}>
              {chartElement}
              <Box sx={{ mt: 2, mb: 2, width: '100%', overflowX: 'auto' }}>
                {tableElement}
              </Box>
            </Box>
          </Grid>
        ) : (
          <Grid container item xs={12} spacing={3} sx={{ px: 0 , alignItems: 'flex-start' }}>
            <Grid item xs={12} lg={6}>
              <Box sx={{ width: '100%' }}>
                {chartElement}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <Box sx={{ width: '100%', overflowX: isScheduleByHour ? 'visible' : 'auto' }}>
                {tableElement}
              </Box>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

const PipelineStatusChart: React.FC<PipelineStatusChartProps> = ({
  title,
  categories,
  series,
  isLoading = false,
  orientation = 'horizontal',
  mode = 'stacked',
  height = DEFAULT_CHART_HEIGHT,
  showBarLabel = true,
  barWidth = DEFAULT_BAR_WIDTH,
  showLegendOnTop = true,
}) => {
  const theme = useTheme();
  const titleColor = theme.palette.widget?.text?.title ?? DEFAULT_TITLE_COLOR;

   const chartOptions = useMemo(
    () => {
      if (!categories || categories.length === 0 || !series || series.length === 0) {
        return null;
      }
      return buildChartOptions({
        categories,
        series,
        orientation,
        mode,
        palette: theme.palette,
        showBarLabel,
        showLegendOnTop,
        barWidth,
      });
    },
    [barWidth, categories, mode, orientation, series, showBarLabel, showLegendOnTop, theme.palette],
  );

  return (
    <Box sx={chartStyles.wrapper}>
      <ChartTitle title={title} color={titleColor} />
      <ChartBody isLoading={isLoading} chartOptions={chartOptions} height={height} />
    </Box>
  );
};

export default PipelineStatusChart;
